import React, { useMemo, useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid,  
	RCCTooltip, 
	RCCInput,
	RCCServicesApi,
	RCCHookToast,
	RCCAlert,
	RCCSelectOptionsCard
} from "react-common-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import { useAuth } from "src/context/Auth/context";
import Select from "src/components/Select";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import { getLocalStorage, setLocalStorage } from "src/utils/localStorage";
import FiltersTagsContainer from "src/components/FiltersTagsContainer";
import { IRefFiltersTagsContainer, TagsTypes } from "src/components/FiltersTagsContainer/interface";
import ButtonExpandGroupers from "src/components/ButtonExpandGroupers";
import { getCookie, setCookie } from "src/utils/cookies";

import DateTimePickerRange from "src/components/DateTimePickerRange";
import formatDate from "src/utils/formatDate";
import LovUsers from "src/components/Lov/Users";
import { useLocation, useParams } from "react-router-dom";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import LovSectors from "src/components/Lov/Sectors";
import LovProcess from "src/components/Lov/Process";
import LovStandards from "src/components/Lov/Standards";
import putUrlSearchParams from "src/utils/putUrlSearchParams";
import AppIcons from "src/components/AppIcons";
import Reports from "src/components/Reports";
import ReportGroupers from "src/components/Reports/interface";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import stringToDate from "src/utils/stringToDate";
import ScreenStandardGroup from "src/Interfaces/screenStandardGroup";
import { AuditType, AuditSituation, GroupersAudit, IFiltersAudits, IGroupersAudits, IGroupersSelectAudits } from "./interface";
import GrouperOfAudits from "./Components/grouperOfAudits";
import ModalAudit from "./Components/ModalAudit";
import { IRefModalAuditAddOrChange } from "./Components/ModalAudit/interface";


const AuditPanel: React.FC = () => { 
	const params = useParams<{
        auditId: string;
    }>();
	
	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const { addToast } = RCCHookToast();
	const { translate } = useTranslation();
	const { user } = useAuth();
	
	const refModalAudits = useRef<IRefModalAuditAddOrChange>(null);
	const filtersTagsRef = useRef<IRefFiltersTagsContainer>(null);

	const [myAudits , setMyAudits] = useState(false);
	const [defaultGrouper, setDefaultGrouper] = useState<GroupersAudit | undefined>(getCookie(ScreenStandardGroup.AUDITS) as GroupersAudit);
	const [layoutList, setLayoutList] = useState<"card" | "list">(getLocalStorage(`AuditsScreenLayoutList-${user.efa005Id}`) === "card" ? "card" : "list");
	const [showFilters, setShowFilters] = useState(false);
	const [showTagsFilters, setShowTagsFilters] = useState(false);
	const [filters, setFilters] = useState<IFiltersAudits>({ 
		filter: objQueryParams?.filter || "", 
		situation: objQueryParams?.situation || "",
		process: objQueryParams?.process || "",
		type: objQueryParams?.type || "",
		sector: objQueryParams?.sector || "",
		standard: objQueryParams?.standard || "",
		startDate: objQueryParams?.startDate ? stringToDate(objQueryParams?.startDate) :  null,
		endDate: objQueryParams?.endDate ? stringToDate(objQueryParams?.endDate) : null,
		responsable: objQueryParams?.responsable || "",
		tags: objQueryParams?.tags || "",
		auditId: params.auditId || ""
	});
	
	const {handleSubmit : handleSubmitFilters, control: controlFilters, register : registerFilters, reset:resetFilters, setValue: setValueFilters } = useForm();
	const buttonSubmitFormGroupersRef = useRef<HTMLButtonElement>(null);

	const { handleSubmit : handleSubmitGroupers, control: controlGroupers } = useForm();
	const [groupers, setGroupers] = useState<IGroupersAudits[]>([]);


	const [groupersSelect, setGroupersSelect] = useState<IGroupersSelectAudits>({
		group: objQueryParams?.group as GroupersAudit || defaultGrouper || GroupersAudit.RESPONSABLE
	});

	const submitGroupers = async (data: any) => {
		putUrlSearchParams({
			...data,
			...filters
		});
		setGroupersSelect({
			...data
		});
	};

	const setAsDefaultGrouper = async () => {
		const {group} = groupersSelect;
		try{
			await RCCServicesApi.postRequest({
				method: "Audits/SetDefaultGrouper",
				body: {
					group
				}
			});
			setDefaultGrouper(group);
			setCookie(ScreenStandardGroup.AUDITS, group);
			addToast({
				color: "green",
				description: translate("Agrupador padrão atualizado com sucesso"),
				title: translate("Sucesso")
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao alterar agrupador padrão"), addToast);
		}
	};

	const { errorFetching,  loading, loadData } = useEffectFetchData<IGroupersAudits[]> (
		"Audits/ListGroupers", 
		{ 
			...groupersSelect,
			...filters,
			startDate: filters.startDate ? formatDate(filters.startDate) : "",
			endDate: filters.endDate ? formatDate(filters.endDate) : ""
		},
		({ data }) => {
			setGroupers(data);
		},
		[groupersSelect, filters],
		true
	);
	
	const submitFilters = (data: any) => {
		const dataForm = data;

		dataForm.filter = tryParseString(data.filter) ?? "";
		dataForm.situation =  tryParseString(data.situation) ?? "";
		dataForm.standard = tryParseString(data.standard) ?? "";
		dataForm.process= tryParseString(data.process) ?? "";
		dataForm.type = tryParseString(data.type) ?? "";
		dataForm.sector = tryParseString(data.sector) ?? "";
		dataForm.responsable = tryParseString(data.responsable) ?? "";
		dataForm.tags = data.tags ? data.tags : "";

		if(myAudits)
			dataForm.responsible = user.efa005Id.toString();

		dataForm.auditId = "";

		putUrlSearchParams(dataForm);
		setFilters(dataForm);
	};
	
	const submitFiltersTags = async () => {
		setFilters((prevState) => {
			const updateState = {...prevState};
			updateState.tags = filtersTagsRef.current?.response ? filtersTagsRef.current?.response : "";
			return updateState;
		});
	};

	const  onCallbackAdd = () => {
		loadData();
	};

	const Groupers = useMemo(() => (
		<>
			{loading ? (
				<Loader />
			) : (
				<>
					{errorFetching ? (
						<RCCAlert 
							color="red"
							title={translate("Atenção")}
							description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
						/>
					): (
						<>
							{groupers.length === 0 ? (
								<RCCAlert 
									title={translate("Atenção")}
									description={translate("Nenhum agrupador encontrado")}
								/>
							): (
								<>
									{groupers.map((grouper) => 
										<GrouperOfAudits
											key={`${grouper.id}-${grouper.id2}-${grouper.id3}`}
											grouper={grouper}
											filters={filters}
											loadGroupers={loadData}
											layoutList={layoutList}	groupersSelect={groupersSelect}
										/>
									)}
								</>
							)}
						</>
					)}
				</>
			)}	
		</>
	), [groupers, layoutList, loading, errorFetching]);

	const tryParseString = (obj: any) => {
		if(typeof obj === "object")
			return obj.value.toString();
		if(typeof obj === "number")
			return obj.toString();
		if(typeof obj !== "string")
			return "";
		return obj;
	};


	const situationOptions = [
		{
			value: "",
			label: translate("Todos")
		},
		{
			value: AuditSituation.OPENED.toString(),
			label: translate("Aberta")
		},
		{
			value: AuditSituation.IN_AUDIT.toString(),
			label: translate("Em Auditoria")
		},
		{
			value: AuditSituation.FINISHED.toString(),
			label: translate("Concluído")
		}	
	];

	const typeOptions = [
		{
			value: "",
			label: translate("Todos")
		},
		{
			value: AuditType.INTERNAL.toString(),
			label: translate("Interna")
		},
		{
			value: AuditType.EXTERNAL.toString(),
			label: translate("Externa")
		}
	];

	const returnObject = (value: any) => {
		if(typeof value === "object"){
			return value;
		}
		if(typeof value === "string"){
			const obj = situationOptions.find((option) => option.value === value);
			if(!obj)
				return "";

			return obj;
		}
		return "";
	};

	const returnObjectType = (value: any) => {
		if(typeof value === "object"){
			return value;
		}
		if(typeof value === "string"){
			const obj = typeOptions.find((option) => option.value === value);
			if(!obj)
				return "";

			return obj;
		}
		return "";
	};

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Painel de Auditorias")}
					icon={() => <AppIcons name="audits" />}	
				>
					
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Nova auditoria")}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => refModalAudits.current?.actionButtonAdd(onCallbackAdd)}
								size="md"
							/>
						</RCCTooltip>
					</CheckPermission>
					
					<RCCTooltip
						text={layoutList === "card" ? translate("Alterar para lista") : translate("Alterar para cards")}
						color="grey"
					>
						<RCCButton
							icon={{
								name: layoutList === "card" ? "list" : "table", 
								color: "#FFF", 
								size: 16
							}}
							color="grey"
							layout="isRounded"
							size="md"
							onClick={() => {
								if(layoutList === "card"){
									setLayoutList("list");
									setLocalStorage(`AuditsScreenLayoutList-${user.efa005Id}`, "list");
									return;
								}
								setLocalStorage(`AuditsScreenLayoutList-${user.efa005Id}`, "card");
								setLayoutList("card");
							}}
						/>
					</RCCTooltip>
					<Reports grouper={ReportGroupers.AUDITS}/>
					<RCCTooltip
						text={`${translate("Filtros")} Tags`}
						color={showTagsFilters ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "tags", color: "#FFF", size: 16}}
							color={showTagsFilters ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowTagsFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>

					<RCCTooltip
						text={translate("Filtros")}
						color={
							showFilters || 
								filters.filter ||
								filters.process ||
								filters.sector ||
								filters.standard ||
								filters.situation || 
								filters.endDate ||
								filters.startDate ||
								filters.responsable || 
								filters.type
								? "blue" : "grey" 
						}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={
								filters.filter ||
								filters.process ||
								filters.sector ||
								filters.standard ||
								filters.situation || 
								filters.endDate ||
								filters.startDate ||
								filters.responsable || 
								filters.type
									? "blue" : "grey" 
							}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
					<ButtonExpandGroupers />	
				</PageTitle>		

				{/* Filters */}			
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ 
						filter: "",
						situation: { value: "", label: translate("Todos")},
						standard: {value : "", label: ""},
						sector: {value : "", label: ""},
						process: {value : "", label: ""},
						type: { value: "", label: translate("Todos")},
						startDate: null,
						endDate : null ,
						responsable: {value : "", label: ""}
					})}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						spacing={1}
						styleInline="margin-bottom: 2.4rem !important;"
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<Select 
								control={controlFilters}
								name="situation" 
								options={situationOptions} 
								label={translate("Situação")}
								defaultValue={filters.situation ? returnObject(filters.situation) : { value: "", label: translate("Todos")}}
								submit="value and label"
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovProcess
								name="process"
								label={translate("Processo")}
								control={controlFilters}
								isClearable
								defaultValue={filters.process}
							/>	
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovStandards
								name="standard"
								label={translate("Norma")}
								control={controlFilters}
								isClearable
								defaultValue={filters.standard}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<Select 
								control={controlFilters}
								name="type" 
								options={typeOptions} 
								label={translate("Tipo")}
								defaultValue={filters.type ? returnObjectType(filters.type) : { value: "", label: translate("Todos")}}
								submit="value and label"
							/>
						</RCCGrid>
						
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovSectors
								name="sector"
								label={translate("Setor")}
								control={controlFilters}
								isClearable
								defaultValue={filters.sector}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<DateTimePickerRange
								control={controlFilters}
								nameInit = "startDate"
								nameEnd = "endDate"
								label = {translate("Período")}
								labelSeparator = {translate("até")}
								type = "datePicker"
								defaultValueInit ={filters.startDate}
								defaultValueEnd ={filters.endDate}
								setValue={setValueFilters}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<LovUsers 
								control={controlFilters}
								name="responsable"
								label={translate("Responsável")}
								defaultValue={ filters.responsable || ""}
								disabled={myAudits}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}	
						>
							<RCCInput
								name="filter" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters.filter ?? ""}
							/>
						</RCCGrid>
					</RCCGrid>
					
				</FiltersContainer>

				{/* Filters Tags */}
				<FiltersTagsContainer 
					ref={filtersTagsRef}
					onSubmitFilters={submitFiltersTags}
					tagType={TagsTypes.AUDITS}
					showFilters={showTagsFilters}
				/>

				<form onSubmit={handleSubmitGroupers(submitGroupers)} style={{marginBottom: "1rem"}}>
					<RCCGrid
						container
						item
						spacing={1}
						justifyContent="space-between"
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							alignItems="baseline"
						>
							<Select 
								control={controlGroupers}
								name="group"
								label={translate("Agrupadores")}
								options={
								  [
										{ value: GroupersAudit.RESPONSABLE, label: `${translate("Responsável")}`},
										{ value: GroupersAudit.SECTOR, label: `${translate("Setor")}`},
										{ value: GroupersAudit.PROCESS, label: `${translate("Processo")}`},
										{ value: GroupersAudit.MANAGER, label: `${translate("Gestor")}`},
										{ value: GroupersAudit.STANDARD, label: `${translate("Norma")}`},
										{ value: GroupersAudit.TYPE, label: `${translate("Tipo")}`}			
									]
								}
								onChangeCapture={() => buttonSubmitFormGroupersRef.current?.click()}
								defaultValue={objQueryParams?.group as GroupersAudit || defaultGrouper || GroupersAudit.RESPONSABLE }
								styleInline="width: auto; flex: 1;"
							/>
							<RCCTooltip
								color="grey"
								text={translate("Definir como agrupador padrão")}
							>
								<RCCButton 
									color="transparent"
									icon={{
										name: "checkBookmark",
										color: "var(--grey)"
									}}
									onClick={setAsDefaultGrouper}
								/>
							</RCCTooltip>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={6}
							lg={3}
							justifyContent="flex-end"
						>
							<RCCSelectOptionsCard
								defaultValue={myAudits ? "S" : "N"}
								options={[{
									value: "N",
									label: translate("Todas as auditorias")
								},{
									value: "S",
									label: translate("Minhas auditorias")
								}]}
								label="&nbsp;"
								onChange={(e) => {
									setValueFilters("responsable", { value: "", label: ""});
									if(e === "S"){
										setMyAudits(true);
										setFilters({
											...filters,
											responsable: user.efa005Id.toString()
										});
										return;
									}
									setMyAudits(false);
									setFilters({
										...filters,
										responsable: ""
									});
								}}
							/>
						</RCCGrid>
					</RCCGrid>
					
					<button 
						aria-label="Submit Form"
						ref={buttonSubmitFormGroupersRef}
						type="submit" 
						style={{ display: "none" }}
					></button>
				</form>
						
				{/* List */}	
				{Groupers}
				
			</RCCGrid>

			<ModalAudit ref={refModalAudits} loadGroupers={loadData} />
		</>
	);
};

export default AuditPanel;
